import { useState, useEffect } from "react";
import type { Expense, CategoryViewModel } from "@/lib/domains";
import { useExpenses } from "@/contexts/expense-context";
import { CategoryService } from "@/lib/domains/category";
import { SUPPORTED_CURRENCIES, getCurrencySymbol } from "@/lib/utils/currency";
import { X } from "lucide-react";
import { Skeleton } from "@/components/skeleton";

interface ExpenseEditModalProps {
    expense: Expense;
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

export function ExpenseEditModal({ expense, isOpen, onClose, onSuccess }: ExpenseEditModalProps) {
    const { updateExpense, currentUser } = useExpenses();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [categories, setCategories] = useState<CategoryViewModel[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [formData, setFormData] = useState({
        amount: expense.amount.toString(),
        currency: expense.currency,
        date: expense.date,
        categoryId: expense.categoryId || "",
        description: expense.description || "",
    });
    const [errors, setErrors] = useState<{ amount?: string; category?: string }>({});

    // Load categories when modal opens
    useEffect(() => {
        async function loadCategories() {
            if (!currentUser || !isOpen) return;

            try {
                setLoadingCategories(true);
                const cats = await CategoryService.getVisibleCategoriesWithPresentation(
                    currentUser.id,
                    'light'
                );
                setCategories(cats);
            } catch (error) {
                console.error('Failed to load categories:', error);
            } finally {
                setLoadingCategories(false);
            }
        }

        loadCategories();
    }, [currentUser, isOpen]);

    // Reset form when expense changes
    useEffect(() => {
        setFormData({
            amount: expense.amount.toString(),
            currency: expense.currency,
            date: expense.date,
            categoryId: expense.categoryId || "",
            description: expense.description || "",
        });
        setErrors({});
    }, [expense]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setErrors({});
        setIsSubmitting(true);

        try {
            const amountValue = parseFloat(formData.amount);

            // Validation
            if (!formData.amount || amountValue <= 0) {
                setErrors({ amount: "Please enter a valid amount greater than 0" });
                setIsSubmitting(false);
                return;
            }

            if (amountValue > 1000000) {
                setErrors({ amount: "Amount is too large" });
                setIsSubmitting(false);
                return;
            }

            if (!formData.categoryId) {
                setErrors({ category: "Please select a category" });
                setIsSubmitting(false);
                return;
            }

            // Check if date is in the future
            const selectedDate = new Date(formData.date);
            const today = new Date();
            today.setHours(23, 59, 59, 999);
            if (selectedDate > today) {
                setErrors({ amount: "Date cannot be in the future" });
                setIsSubmitting(false);
                return;
            }

            // Get category name for backward compatibility
            const category = categories.find(c => c.id === formData.categoryId);

            // Build updates object (only changed fields)
            const updates: Partial<Omit<Expense, "id" | "userId" | "createdAt">> = {};

            if (parseFloat(formData.amount) !== expense.amount) {
                updates.amount = parseFloat(formData.amount);
            }
            if (formData.currency !== expense.currency) {
                updates.currency = formData.currency;
            }
            if (formData.date !== expense.date) {
                updates.date = formData.date;
            }
            if (formData.categoryId !== expense.categoryId) {
                updates.categoryId = formData.categoryId;
                updates.category = category?.displayName || 'Other'; // Legacy field
            }
            if (formData.description.trim() !== (expense.description || "")) {
                updates.description = formData.description.trim() || undefined;
            }

            // Only update if there are changes
            if (Object.keys(updates).length === 0) {
                onClose();
                return;
            }

            await updateExpense(expense.id, updates);
            onSuccess?.();
            onClose();
        } catch (err: unknown) {
            setErrors({
                amount:
                    err instanceof Error
                        ? `Failed to update expense: ${err.message}`
                        : "Failed to update expense. Please try again."
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Edit Expense</h2>
                    <button
                        onClick={onClose}
                        className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        aria-label="Close"
                    >
                        <X size={24} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6">
                    {/* Amount */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Amount *
                        </label>
                        <div className="flex gap-2">
                            <div className="flex-1 flex items-center border border-gray-300 rounded-lg px-3 py-2 focus-within:border-teal-500 focus-within:ring-1 focus-within:ring-teal-500">
                                <span className="text-gray-600 mr-2">
                                    {getCurrencySymbol(formData.currency)}
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    value={formData.amount}
                                    onChange={(e) => {
                                        setFormData({ ...formData, amount: e.target.value });
                                        if (errors.amount) setErrors({});
                                    }}
                                    className="w-full outline-none text-lg font-mono"
                                    autoFocus
                                    required
                                />
                            </div>
                            <select
                                value={formData.currency}
                                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                            >
                                {SUPPORTED_CURRENCIES.map((curr) => (
                                    <option key={curr} value={curr}>
                                        {curr}
                                    </option>
                                ))}
                            </select>
                        </div>
                        {errors.amount && (
                            <p className="text-red-500 text-sm mt-2">{errors.amount}</p>
                        )}
                    </div>

                    {/* Date */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Date *
                        </label>
                        <input
                            type="date"
                            value={formData.date}
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                            required
                        />
                    </div>

                    {/* Category */}
                    <div className="mb-5">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category *
                        </label>
                        {loadingCategories ? (
                            <Skeleton className="h-10 w-full" />
                        ) : (
                            <select
                                value={formData.categoryId}
                                onChange={(e) => {
                                    setFormData({ ...formData, categoryId: e.target.value });
                                    if (errors.category) setErrors({});
                                }}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none bg-white"
                                required
                            >
                                <option value="">Select a category</option>
                                {categories.map((cat) => (
                                    <option key={cat.id} value={cat.id}>
                                        {cat.icon} {cat.displayName}
                                    </option>
                                ))}
                            </select>
                        )}
                        {errors.category && (
                            <p className="text-red-500 text-sm mt-2">{errors.category}</p>
                        )}
                    </div>

                    {/* Description */}
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description (optional)
                        </label>
                        <textarea
                            placeholder="Add a note..."
                            value={formData.description}
                            onChange={(e) =>
                                setFormData({ ...formData, description: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none resize-none"
                            rows={3}
                            maxLength={500}
                        />
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex-1 px-4 py-2 text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg font-medium transition-colors"
                        >
                            {isSubmitting ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
